// src/components/layout/Header.tsx
import React, { useState } from 'react';
import { ViewId } from '../../types/navigation';
import { useAudio } from '../../audio/useAudio';
import { Navigation } from './Navigation';
import { MobileNav } from './MobileNav';

interface HeaderProps {
  currentView: ViewId;
  onViewChange: (view: ViewId) => void;
}

export const Header: React.FC<HeaderProps> = ({ currentView, onViewChange }) => {
  const { isPlaying, isTransitioning, toggleDrone } = useAudio();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <>
      <header className="sticky top-0 z-40 w-full border-b border-iron-dark/80 bg-obsidian-deep/90 backdrop-blur-md transition-colors duration-300">
        <div className="mx-auto flex h-18 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          
          {/* Brand & Monogram */}
          <button
            onClick={() => onViewChange('sanctuary')}
            className="group flex items-center gap-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-bright/50 rounded-sm"
          >
            {/* Medallion Logo */}
            <div className="flex h-11 w-11 items-center justify-center p-0.5 transition-all duration-300">
              <img
                src="/assets/belial_logo_emblem.png"
                alt="King Belial Medallion"
                className="h-full w-full object-contain drop-shadow-[0_0_10px_rgba(201,148,74,0.4)] transition-transform duration-300 group-hover:scale-110"
              />
            </div>

            <div>
              <span className="block font-display text-lg sm:text-xl font-bold tracking-monumental text-parchment-light group-hover:text-gold-bright transition-colors">
                BELIAL
              </span>
              <span className="hidden sm:block font-display text-[9px] tracking-[0.22em] text-iron-steel group-hover:text-gold-ancient/80 transition-colors uppercase">
                Current of the Unyoked
              </span>
            </div>
          </button>

          {/* Desktop Navigation Links (>= md) */}
          <Navigation
            currentView={currentView}
            onViewChange={onViewChange}
            className="hidden md:flex"
          />

          {/* Controls: Audio Drone Toggle & Mobile Menu */}
          <div className="flex items-center gap-3">
            {/* Liturgical Audio Drone Toggle */}
            <button
              onClick={() => toggleDrone()}
              disabled={isTransitioning}
              aria-label="Toggle Liturgical Sub-Bass Drone"
              aria-pressed={isPlaying}
              className={`group flex items-center gap-2.5 rounded border px-3 py-1.5 transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-ember-flare ${
                isPlaying
                  ? 'border-ember-flare/60 bg-ember-dark/25 text-ember-spark shadow-glow-ember'
                  : 'border-iron-base bg-obsidian-surface/60 text-parchment-dim hover:border-iron-steel hover:text-parchment-light'
              }`}
            >
              {/* Equalizer Waveform Bars (4 bars) */}
              <div className="flex items-end gap-[3px] h-4 w-4" aria-hidden="true">
                <span className={`w-[2.5px] rounded-full bg-current transition-all ${isPlaying ? 'animate-equalizer-1' : 'h-1.5'}`} />
                <span className={`w-[2.5px] rounded-full bg-current transition-all ${isPlaying ? 'animate-equalizer-2' : 'h-3'}`} />
                <span className={`w-[2.5px] rounded-full bg-current transition-all ${isPlaying ? 'animate-equalizer-3' : 'h-2'}`} />
                <span className={`w-[2.5px] rounded-full bg-current transition-all ${isPlaying ? 'animate-equalizer-4' : 'h-3.5'}`} />
              </div>

              <div className="text-left leading-none">
                <span className="block font-display text-[10px] tracking-widest uppercase">
                  {isPlaying ? 'DRONE RESONATING' : 'LITURGICAL DRONE'}
                </span>
                <span className="hidden lg:block text-[8.5px] font-serif italic text-gold-ancient/80">
                  {isPlaying ? 'Subterranean Tone' : 'Silent Altar'}
                </span>
              </div>
            </button>

            {/* Mobile Hamburger Button (< md) */}
            <button
              onClick={() => setMobileNavOpen(true)}
              className="flex h-10 w-10 items-center justify-center rounded border border-iron-dark bg-obsidian-surface/80 text-parchment-dim hover:border-iron-steel hover:text-parchment-light md:hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-bright"
              aria-label="Open Navigation Menu"
              aria-expanded={mobileNavOpen}
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="6" x2="21" y2="6" strokeLinecap="round" />
                <line x1="3" y1="12" x2="21" y2="12" strokeLinecap="round" />
                <line x1="3" y1="18" x2="21" y2="18" strokeLinecap="round" />
              </svg>
            </button>
          </div>

        </div>
      </header>

      {/* Mobile Navigation Drawer */}
      <MobileNav
        isOpen={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        currentView={currentView}
        onViewChange={(view) => {
          onViewChange(view);
          setMobileNavOpen(false);
        }}
      />
    </>
  );
};
